import React, { useEffect, useState, useContext } from 'react';
import '../../../campaign/styles/Broadcastextend.css';

import { BroadcastSegmentList } from '../../../../constant/app/campaign/Broadcast';

import { useAspSelector, useAspDispatch } from '../../../../test/jest-redux-hooks';
import { setExportPolling } from '../../../../reduxToolkit/broadcastSlice';
import {
  broadCastContactListApi,
  broadcastSegmentApi,
  getBroadcastPartApi,
  templateList,
} from '../../../campaign/api/Api';

// Import centralized Excel export helper for consistent report generation
import { exportBroadcastReport } from '../../../../helper/excelExport';

// Export report polling
import {
  exportReportStatus,
  exportReportUserSeen,
} from '../../api/broadcastsApi';
import useExportReportPolling from '../../../../hooks/useExportReportPolling';
import NotificationContext from '../../../../providers/NotificationProvider';
import {
  showSuccessToast,
  showErrorToast,
} from '../../../../components/commonComponents/gxAntdToast/toastUtils';
import BroadcastMessageView from './BroadcastMessageView';
import BroadcastSegmentView from './BroadcastSegmentView';
import ContactView from './ContactView';
import SegmentDataFile from './SegmentDataFile';
import SegmentFile from './SegmentFile';
import SegmentOverview from './SegmentOverview';
import SettingBroadView from './SettingBroadView';

const BroadcastMetricsHeader = ({ broadcastId, onBroadcastDataLoaded }) => {
  const [clickedSegment, setClickedSegment] = useState(1);
  const [segment, setSegment] = useState([]);
  const [contactList, setContactList] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [isFilter, setIsFilter] = useState('');
  const [contactDetails, setContactDetails] = useState({});
  const [fileId, setFileId] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [selectedTemplate, setSelectedTemplate] = useState({});
  const [isLoad, setIsLoad] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoader, setIsLoader] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [products, setProducts] = useState([]);
  const [orderDetails, setOrderDetails] = useState({});
  const templateId = segment?.settings?.template_id;

  const dispatch = useAspDispatch();
  const { currentBrand } = useAspSelector((state) => state.app);
  const isExportPolling = useAspSelector((state) => state.broadcast.isExportPolling);
  const notification = useContext(NotificationContext);

  useExportReportPolling({
    isPolling: isExportPolling,
    fetchStatus: () =>
      exportReportStatus(currentBrand?.brand_id, 'broadcast_contacts'),
    markSeen: (reportId) =>
      exportReportUserSeen(currentBrand?.brand_id, {
        export_report_id: reportId,
      }),
    onComplete: () => {
      showSuccessToast(
        notification,
        'Report is ready! A download link has been sent to your email.',
        5,
      );
    },
    onError: () => {
      showErrorToast(
        notification,
        'Report generation failed. Please try again.',
        5,
      );
    },
    onStop: () => dispatch(setExportPolling(false)),
  });
  const [response, setResponse] = useState([]);

  const handleSegmentClick = (dataId) => {
    setClickedSegment(dataId);
  };

  const handleContactClick = (item) => {
    setFileId(item?.id);
    setContactDetails(item);
  };

  const broadcastPartId =
    fileId.length === 0 ? response?.[0]?.broadcastPartId : fileId;

  useEffect(() => {
    if (segment && segment.id && !response.length) {
      getBroadcastPartApi(segment.id, currentBrand?.brand_id)
        .then((res) => {
          if (res && res.data?.broadcast_parts?.length > 0) {
            const response = res.data.broadcast_parts;
            setResponse(response);
          }
        })
        .catch((error) => {
          console.error('Error fetching data:', error);
        });
    }
  }, [segment, response]);

  const brandId = currentBrand?.brand_id;

  useEffect(() => {
    if (brandId && broadcastId) {
      // setIsLoad(true);
      // For standalone broadcasts, we don't pass campaign_id
      broadcastSegmentApi(broadcastId, brandId, null)
        .then((res) => {
          setIsLoad(false);
          if (res && res.data) {
            setSegment(res.data.broadcast);
            setProducts(res.data?.payment_details?.products);
            setOrderDetails(res.data?.payment_details?.order_details);
            setIsLoad(false);

            // Pass broadcast data to parent component
            if (onBroadcastDataLoaded) {
              onBroadcastDataLoaded(res.data.broadcast);
            }
          } else {
            setIsLoad(false);
          }
        })
        .finally(() => {
          setIsLoad(false);
        });
    }
  }, [brandId, broadcastId]);

  const fetchBroadcastContactList = (
    brandId,
    broadcastId,
    broadcastPartId,
    payload,
  ) => {
    setIsLoading(true);
    if (brandId && broadcastId) {
      broadCastContactListApi(
        brandId,
        broadcastId,
        broadcastPartId,
        payload,
      ).then((res) => {
        if (res && res?.data) {
          setContactList(res?.data);
          setCount(res.data.broadcast_contacts_count);
          setIsLoading(false);
        }
      });
    }
  };

  const loadNextPage = (page) => {
    if (contactList?.broadcast_contacts?.length < count) {
      fetchBroadcastContactList(
        currentBrand?.brand_id,
        broadcastId,
        broadcastPartId,
        {
          page: page,
          limit: pageSize,
          status: selectedStatus,
        },
      );
    }
  };

  const handlePageChange = (page) => {
    if (contactList?.broadcast_contacts?.length < count) {
      loadNextPage(page);
      setPage(page);
      if (selectedStatus) {
        fetchBroadcastContactList(
          currentBrand?.brand_id,
          broadcastId,
          broadcastPartId,
          {
            page: page,
            limit: pageSize,
            status: selectedStatus,
          },
        );
      }
    }
  };

  const handlePageChangeRow = (pageSize) => {
    const pageSizeLimit = parseInt(pageSize);
    loadNextPage(pageSize);
    setPage(page);
    setPageSize(pageSizeLimit);
  };

  useEffect(() => {
    if (broadcastPartId) {
      fetchBroadcastContactList(brandId, broadcastId, broadcastPartId, {
        page: page,
        limit: pageSize,
        status: selectedStatus,
      });
    }
  }, [brandId, broadcastPartId]);

  /**
   * Downloads broadcast contacts as an Excel report with summary section
   *
   * Fetches all contacts for the current broadcast part and generates
   * a professional Excel report using the centralized export helper.
   * The report includes summary metrics at the top and detailed data below.
   *
   * @param {Object} payload - Additional query parameters for the API
   */
  const downloadBroadcastContacts = async (payload) => {
    setIsLoader(true);
    try {
      if (brandId && broadcastId) {
        const res = await broadCastContactListApi(
          brandId,
          broadcastId,
          broadcastPartId,
          {
            ...payload,
            download_all: true,
            status: selectedStatus,
          },
        );

        if (res && res?.data) {
          // Large file — dispatched to background worker
          if (res.data?.export_dispatched) {
            if (res.data?.already_in_progress) {
              showErrorToast(
                notification,
                res.data.message || 'A report is already being generated.',
                5,
              );
            } else {
              showSuccessToast(
                notification,
                res.data.message ||
                  'Report is being generated. You will receive an email with the download link.',
                5,
              );
            }
            dispatch(setExportPolling(true));
            return;
          }

          // Small file — generate Excel client-side (existing flow)
          const allContacts = res?.data?.broadcast_contacts || [];
          const statusCounts = res?.data?.status_counts || {};

          const result = await exportBroadcastReport({
            contacts: allContacts,
            counts: {
              total: statusCounts.total || allContacts.length,
              delivered: statusCounts.delivered || 0,
              opened: statusCounts.opened || 0,
              lead: statusCounts.lead || 0,
              clicked: statusCounts.clicked || 0,
              bounced: statusCounts.bounced || 0,
              skipped: statusCounts.credit_blocked || 0,
            },
            fileName: segment?.name || 'Broadcast_Report',
            sheetName: 'Broadcast Report',
          });

          if (!result.success) {
            console.error('Export failed:', result.message);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching broadcast contacts:', error);
    } finally {
      setIsLoader(false);
    }
  };

  const templateSearch = (value) => {
    setIsFilter(value);
    fetchBroadcastContactList(
      currentBrand?.brand_id,
      broadcastId,
      broadcastPartId,
      {
        filter: value,
        name: value,
        status: selectedStatus,
      },
    );
  };

  const onFilterChange = (status) => {
    const updatedStatus = selectedStatus.includes(status)
      ? selectedStatus.filter((s) => s !== status)
      : [...selectedStatus, status];
    setSelectedStatus(updatedStatus);
    fetchBroadcastContactList(
      currentBrand?.brand_id,
      broadcastId,
      broadcastPartId,
      {
        status: updatedStatus,
        limit: pageSize,
        page: page,
      },
    );
  };

  const handleContactClick1 = (item) => {
    setContactDetails(item);
    setPageSize(parseInt(item?.value));
    fetchBroadcastContactList(
      currentBrand?.brand_id,
      broadcastId,
      broadcastPartId,
      {
        limit: parseInt(item?.value),
        page: 1,
        status: selectedStatus,
      },
    );
  };

  const fetchSingleTemplateLists = (brandId, templateId) => {
    if (brandId) {
      templateList(brandId, templateId)
        .then((res) => {
          if (res?.status === 200) {
            setSelectedTemplate(res?.data?.wapp_template);
          }
        })
        .finally(() => {
          setIsLoad(false);
        });
    } else {
      setSelectedTemplate([]);
    }
  };

  useEffect(() => {
    if (templateId) {
      fetchSingleTemplateLists(currentBrand?.brand_id, templateId);
    }
  }, [currentBrand, templateId]);

  return (
    <div
      className="segmentHeader-container flex-column"
      style={{ border: '1px solid var(--font-50)' }}
    >
      <div className=" mmm flex-row  w-100  align-center">
        {BroadcastSegmentList?.map((data) => (
          <div
            className={`ss flex-row align-center space-evenly pointer ${
              clickedSegment === data.id ? 'green-background' : ''
            }`}
            style={{
              borderBottom:
                clickedSegment === data.id
                  ? '3px solid var(--primary)'
                  : 'none',
            }}
            key={data?.id}
            onClick={() => handleSegmentClick(data.id)}
          >
            <span className="cursor-pointer">{data?.name}</span>
          </div>
        ))}
      </div>
      <div
        className="overview"
        style={{ padding: '20px 30px', width: '100%', height: '100%' }}
      >
        {clickedSegment === 1 && (
          <div>
            <SegmentOverview segment={segment} />
            <SegmentFile segment={segment} isLoad={isLoad} />
          </div>
        )}
        {clickedSegment === 2 && <SettingBroadView segment={segment} />}
        {clickedSegment === 3 && (
          <SegmentDataFile segment={segment} response={response} />
        )}
        {clickedSegment === 4 && (
          <BroadcastSegmentView segment={segment} response={response} />
        )}
        {clickedSegment === 5 && (
          <BroadcastMessageView
            segment={segment}
            selectedTemplate={selectedTemplate}
            setSelectedTemplate={setSelectedTemplate}
            availableProducts={products}
            products={products}
            orderDetails={orderDetails}
          />
        )}

        {clickedSegment === 6 && (
          <ContactView
            contactList={contactList}
            handlePageChange={handlePageChange}
            page={page}
            setPage={setPage}
            count={count}
            loadNextPage={loadNextPage}
            templateSearch={templateSearch}
            isFilter={isFilter}
            setIsFilter={setIsFilter}
            contactDetails={contactDetails}
            setContactDetails={setContactDetails}
            handleContactClick={handleContactClick}
            fileId={fileId}
            pageSize={pageSize}
            setPageSize={setPageSize}
            handlePageChangeRow={handlePageChangeRow}
            handleContactClick1={handleContactClick1}
            isLoading={isLoading}
            onFilterChange={onFilterChange}
            selectedStatus={selectedStatus}
            segment={segment}
            downloadBroadcastContacts={downloadBroadcastContacts}
            isLoader={isLoader}
          />
        )}
      </div>
    </div>
  );
};

export default BroadcastMetricsHeader;
